import { cloneDeep, isEqual, unset } from 'lodash'
import { axiosRequest } from '@/plugins/axios-common'
import { handleError } from '@/store/handle-error'

import {
  DocketEntryEditInviteActions,
  DocketEntryEditInviteGetters,
  DocketEntryEditInviteMutations,
  DocketEntryEditInviteState,
} from './invite'

const DIALOG_EVENT_MAPPINGS = {
  Appointment: 'setDocketEntryEditorVisibility',
  Event: 'setDocketEntryEditorVisibility',
  Invite: 'setDocketEntryEditorInviteVisibility',
  Note: 'setDocketEntryEditorVisibility',
  Task: 'setDocketEntryEditorVisibility',
  default: 'setDocketEntryEditorVisibility',
}

const emptyDocketEntry = {
  action_date: null,
  action_type: null,
  agency_num: null,
  appointment_location: null,
  attendances: [],
  checklist: null,
  child_docket_entry_relations: [],
  comment: '',
  docket_entries_assignments: [],
  docket_entry_notifications: [],
  docket_entry_recipient: {
    party_id: null,
  },
  docket_entry_sharepoint_files: [],
  docket_entry_type_id: null,
  event_datetime_end: null,
  event_datetime_start: null,
  event_time_end: null,
  event_time_start: null,
  files: [],
  has_scheduled_text: false,
  id: null,
  legal_case_id: null,
  modified_workflows: [],
  parent_docket_entry_relations: [],
  require_all_done: false,
  skip_workflow: false,
  timezone: null,
}

export const DocketEntryEditState = {
  docketEntryEdit: {
    actionType: '',
    docketEntryId: null,
    cloneMode: false,
    editedDocketEntry: null,
    isVisible: false,
    legalCaseId: null,
    originalDocketEntry: null,
    parentDocketEntryId: null,
    rescheduleMode: false,
    startingStep: 1,
    ...DocketEntryEditInviteState,
  },
  textMessageEditor: {
    isVisible: false,
  },
}

export const allowedNotificationEvents = action => {
  if (action === 'reschedule') {
    return [
      'completed',
      'reminder',
      'rescheduled',
    ]
  }

  if (action === 'edit') {
    return [
      'completed',
      'reminder',
      'notification',
    ]
  }

  return [
    'completed',
    'created',
    'reminder',
    'notification',
  ]
}

export const DocketEntryEditActions = {
  ...DocketEntryEditInviteActions,

  resetEditedDocketEntryData: async ({ commit, state }, initialValues) => {
    const data = cloneDeep(initialValues)

    if (state.docketEntryEdit.parentDocketEntryId && !data.child_docket_entry_relations?.
      find(cder => cder.child_docket_entry_id === state.docketEntryEdit.parentDocketEntryId)
    ) {
      if (!data.child_docket_entry_relations) data.child_docket_entry_relations = []

      data.child_docket_entry_relations.push({
        child_docket_entry_id: state.docketEntryEdit.parentDocketEntryId,
        is_protected: true,
        parent_docket_entry_id: null,
      })
    }

    return commit('setDocketEntryEditData', {
      ...data,
      action_type: state.docketEntryEdit.actionType,
      legal_case_id: state.docketEntryEdit.legalCaseId,
    })
  },

  resetEditedDocketEntryWithOriginalData: async ({ commit }) => commit('resetDocketEntryEditionData'),

  getDocketEntryForEdition: async ({ commit }, id) => {
    return axiosRequest.get(`/docket_entries/${id}`).
      then(({ data }) => commit('setDocketEntryEditData', data)).
      catch(handleError)
  },

  getDocketEntryForCloning: async ({ commit }, id) => {
    return axiosRequest.get(`/docket_entries/${id}`).
      then(({ data }) => {
        commit('setDocketEntryEditData', data)
        commit('setEditedDocketEntryId', null)
      }).
      catch(handleError)
  },

  saveEditedDocketEntry: async ({ dispatch, commit, state }, options = {}) => {
    const baseUrl = options.url || 'docket_entries'
    const baseUrl2 = options.url || 'docket_entries2'
    const data = cloneDeep(state.docketEntryEdit.editedDocketEntry)

    data.child_docket_entry_relations_attributes = data.child_docket_entry_relations
    unset(data, 'child_docket_entry_relations')

    data.assignee_ids = data.docket_entries_assignments.reduce((res, dea) => {
      if (!dea._destroy) res.push(dea.user_id)
      return res
    }, [])
    unset(data, 'docket_entries_assignments')

    data.attendee_ids = data.attendances.reduce((res, dea) => {
      if (!dea._destroy) res.push(dea.party_id)
      return res
    }, [])
    unset(data, 'attendances')

    data.docket_entry_notifications_attributes = data.docket_entry_notifications
    unset(data, 'docket_entry_notifications')

    if (data.docket_entry_recipient.party_id) data.docket_entry_recipient_attributes = data.docket_entry_recipient
    unset(data, 'docket_entry_recipient')

    data.attachment_ids = data.files.map(e => e.id)

    let request = null

    if (state.docketEntryEdit.editedDocketEntry.id) {
      request = axiosRequest.patch(
        `/${baseUrl2}/${state.docketEntryEdit.editedDocketEntry.id}`,
        { docket_entry: data }
      )
    } else if (state.docketEntryEdit.cloneMode) {
      request = axiosRequest.post(
        `/${baseUrl}/${state.docketEntryEdit.originalDocketEntry.id}/clone`,
        { docket_entry: data }
      )
    } else if (state.docketEntryEdit.rescheduleMode) {
      request = axiosRequest.post(
        `/${baseUrl}/${state.docketEntryEdit.originalDocketEntry.id}/reschedule`,
        { docket_entry: data }
      )
    } else {
      request = axiosRequest.post(`/${baseUrl2}`, { docket_entry: data })
    }

    return request.
      then(({ data }) => {
        if (state.docketEntryEdit.parentDocketEntryId) {
          dispatch('markDocketEntryAsDone', state.docketEntryEdit.parentDocketEntryId)
        }

        return commit('updateDocketEntryEditData', data)
      }).
      catch(handleError)
  },

  openDocketEntryEditor: async ({ commit }, {
    actionType,
    cloneMode,
    docketEntryId,
    legalCaseId,
    parentDocketEntryId,
    rescheduleMode,
    startingStep,
  }) => {
    if (cloneMode && rescheduleMode) {
      throw new Error('Clone and Reschedule modes cannot be selected at the same time.')
    }

    commit('setDocketEntryEditActionType', actionType)
    commit('setDocketEntryEditCloneMode', !!cloneMode)
    commit('setDocketEntryEditRescheduleMode', !!rescheduleMode)
    commit('setDocketEntryEditDocketEntryId', docketEntryId)
    commit('setDocketEntryEditLegalCaseId', legalCaseId)
    commit('setDocketEntryEditParentDocketEntryId', parentDocketEntryId)
    commit('setDocketEntryEditRescheduleMode', !!rescheduleMode)
    commit('setDocketEntryEditStartingStep', startingStep)
    commit(DIALOG_EVENT_MAPPINGS[actionType || 'default'], true)
  },

  closeDocketEntryEditor: async ({ commit, state }) => {
    commit(DIALOG_EVENT_MAPPINGS[state.docketEntryEdit.actionType || 'default'], false)
  },

  openTextMessageEditor: async ({ commit }, {
    cloneMode,
    docketEntryId,
    legalCaseId,
    parentDocketEntryId,
  }) => {
    commit('setDocketEntryEditActionType', 'TextMessage')
    commit('setDocketEntryEditCloneMode', !!cloneMode)
    commit('setDocketEntryEditDocketEntryId', docketEntryId)
    commit('setDocketEntryEditLegalCaseId', legalCaseId)
    commit('setDocketEntryEditParentDocketEntryId', parentDocketEntryId)
    commit('setDocketEntryEditRescheduleMode', false)
    commit('setTextMessageEditorVisibility', true)
  },

  closeTextMessageEditor: async ({ commit }) => {
    commit('setTextMessageEditorVisibility', false)
  },
}

export const DocketEntryEditMutations = {
  ...DocketEntryEditInviteMutations,

  resetDocketEntryEditionData: state => {
    state.docketEntryEdit.editedDocketEntry = cloneDeep({
      ...state.docketEntryEdit.originalDocketEntry,
      attendances: state.docketEntryEdit.cloneMode ? [] : state.docketEntryEdit.originalDocketEntry.attendances,
      docket_entries_assignments: state.docketEntryEdit.cloneMode || state.docketEntryEdit.rescheduleMode
        ? state.docketEntryEdit.originalDocketEntry.docket_entries_assignments.map(des => {
          return {
            user_id: des.user_id,
          }
        })
        : state.docketEntryEdit.originalDocketEntry.docket_entries_assignments,
      id: state.docketEntryEdit.cloneMode || state.docketEntryEdit.rescheduleMode
        ? null
        : state.docketEntryEdit.docketEntryId,
      event_time_end: state.docketEntryEdit.rescheduleMode
        ? null
        : state.docketEntryEdit.originalDocketEntry?.event_time_end,
      event_time_start: state.docketEntryEdit.rescheduleMode
        ? null
        : state.docketEntryEdit.originalDocketEntry?.event_time_start,
    })
  },

  setDocketEntryEditActionType: (state, actionType) => {
    state.docketEntryEdit.actionType = actionType
  },

  setDocketEntryEditCloneMode: (state, cloneMode) => {
    state.docketEntryEdit.cloneMode = cloneMode
  },

  setDocketEntryEditData: (state, payload) => {
    state.docketEntryEdit.originalDocketEntry = cloneDeep({
      ...emptyDocketEntry,
      ...payload,
    })

    state.docketEntryEdit.editedDocketEntry = cloneDeep({
      ...state.docketEntryEdit.originalDocketEntry,
      attendances: state.docketEntryEdit.cloneMode ? [] : state.docketEntryEdit.originalDocketEntry.attendances,
      docket_entries_assignments: state.docketEntryEdit.cloneMode || state.docketEntryEdit.rescheduleMode
        ? state.docketEntryEdit.originalDocketEntry.docket_entries_assignments.map(des => {
          return {
            user_id: des.user_id,
          }
        })
        : state.docketEntryEdit.originalDocketEntry.docket_entries_assignments,
      id: state.docketEntryEdit.cloneMode || state.docketEntryEdit.rescheduleMode
        ? null
        : state.docketEntryEdit.docketEntryId,
      event_time_end: state.docketEntryEdit.rescheduleMode
        ? null
        : state.docketEntryEdit.originalDocketEntry.event_time_end,
      event_time_start: state.docketEntryEdit.rescheduleMode
        ? null
        : state.docketEntryEdit.originalDocketEntry.event_time_start,
    })
  },

  setDocketEntryEditDocketEntryId: (state, docketEntryId) => {
    state.docketEntryEdit.docketEntryId = docketEntryId
  },

  setDocketEntryEditLegalCaseId: (state, legalCaseId) => {
    state.docketEntryEdit.legalCaseId = legalCaseId
  },

  setDocketEntryEditParentDocketEntryId: (state, parentDocketEntryId) => {
    state.docketEntryEdit.parentDocketEntryId = parentDocketEntryId
  },

  setDocketEntryEditRescheduleMode: (state, rescheduleMode) => {
    state.docketEntryEdit.rescheduleMode = rescheduleMode
  },

  setDocketEntryEditStartingStep: (state, startingStep) => {
    state.docketEntryEdit.startingStep = startingStep
  },

  setDocketEntryEditorVisibility: (state, visible) => {
    state.docketEntryEdit.isVisible = visible
  },

  setEditedDocketEntryId: (state, id) => {
    state.docketEntryEdit.editedDocketEntry.id = id
  },

  setTextMessageEditorVisibility: (state, visible) => {
    state.textMessageEditor.isVisible = visible
  },

  updateDocketEntryEditData: (state, payload) => {
    state.docketEntryEdit.originalDocketEntry = {
      ...state.docketEntryEdit.originalDocketEntry,
      ...cloneDeep(payload),
    }

    state.docketEntryEdit.editedDocketEntry = {
      ...state.docketEntryEdit.editedDocketEntry,
      ...cloneDeep(payload),
    }
  },
}

export const DocketEntryEditGetters = {
  ...DocketEntryEditInviteGetters,

  docketEntryEditorActionType: state => {
    return state.docketEntryEdit.editedDocketEntry?.action_type || state.docketEntryEdit.actionType
  },

  docketEntryEditorDocketEntryId: state => {
    return state.docketEntryEdit.docketEntryId
  },

  docketEntryEditorLegalCaseId: state => {
    return state.docketEntryEdit.legalCaseId
  },

  docketEntryEditorStartingStep: state => state.docketEntryEdit.startingStep,

  editedDocketEntry: state => {
    return state.docketEntryEdit.editedDocketEntry
  },

  editedDocketEntryWasChanged: state => !isEqual(
    state.docketEntryEdit.originalDocketEntry,
    state.docketEntryEdit.editedDocketEntry
  ),

  isDocketEntryEditorInCloneMode: state => state.docketEntryEdit.cloneMode,
  isDocketEntryEditorInRescheduleMode: state => state.docketEntryEdit.rescheduleMode,
  isDocketEntryEditorVisible: state => state.docketEntryEdit.isVisible,
  isTextMessageEditorVisible: state => state.textMessageEditor.isVisible,
  originalDocketEntry: state => state.docketEntryEdit.originalDocketEntry,
}
